import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Panel } from '@usacow/ds';
import { api, type ContentItem } from '../../lib/api';

const LIFECYCLE_TONE: Record<string, string> = {
  published: 'correct',
  approved: 'info',
  human_reviewed: 'info',
  draft: 'neutral',
  deprecated: 'warning',
  rolled_back: 'incorrect',
};

export function Inventory() {
  const [items, setItems] = useState<ContentItem[] | null>(null);
  const [analytics, setAnalytics] = useState<{
    byLifecycle: { lifecycle: string; n: number }[];
    openIssues: number;
  } | null>(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    api
      .adminContent()
      .then((r) => setItems(r.items))
      .catch(() => setForbidden(true));
    api
      .adminAnalytics()
      .then(setAnalytics)
      .catch(() => {});
  }, []);

  if (forbidden)
    return (
      <div className="scr">
        <h1 className="scr-h">Content-ops</h1>
        <p className="dim">You need a content-ops role. In dev, set one from Settings.</p>
      </div>
    );

  return (
    <div className="scr">
      <div className="scr-head">
        <div>
          <div className="t-eyebrow">content-ops</div>
          <h1 className="scr-h">Content inventory</h1>
        </div>
        {analytics && (
          <div className="adm-stats">
            <span>
              <b>{analytics.openIssues}</b> open issues
            </span>
            {analytics.byLifecycle.map((l) => (
              <span key={l.lifecycle}>
                <b>{l.n}</b> {l.lifecycle}
              </span>
            ))}
          </div>
        )}
      </div>
      <Panel title="All content">
        {!items ? (
          <div className="dim">Loading…</div>
        ) : items.length === 0 ? (
          <p className="dim">
            No published content yet. Seed a scenario from the student app (dev).
          </p>
        ) : (
          <table className="coach-table">
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Ver</th>
                <th>Division</th>
                <th>Difficulty</th>
                <th>Lifecycle</th>
                <th>Issues</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={`${it.scenario_id}-${it.version}`}>
                  <td>
                    <Link to={`/admin/content/${it.scenario_id}/${it.version}`}>
                      {it.scenario_id}
                    </Link>
                  </td>
                  <td className="mono">{it.version}</td>
                  <td>{it.division}</td>
                  <td className="mono">{it.difficulty}</td>
                  <td>
                    <Badge
                      tone={(LIFECYCLE_TONE[it.lifecycle] ?? 'neutral') as never}
                      variant="outline"
                    >
                      {it.lifecycle}
                    </Badge>
                  </td>
                  <td className="mono">{it.open_issues || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
